import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Turnstile } from "@marsidev/react-turnstile";
import { useAdmin } from "./AdminContext";
import { Lock, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ENV_TURNSTILE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

export default function AdminLogin() {
  const { login, token, loading } = useAdmin();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [tsToken, setTsToken] = useState("");
  const [siteKey, setSiteKey] = useState(ENV_TURNSTILE_KEY);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const tsRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/public/site-config`).then(r => setSiteKey(r.data.turnstile_site_key || "")).catch(() => {});
  }, []);

  useEffect(() => {
    if (!loading && token) navigate("/admin", { replace: true });
  }, [loading, token, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!username.trim() || !password) {
      setError("Kullanıcı adı ve şifre zorunlu.");
      return;
    }
    if (siteKey && !tsToken) {
      setError("Lütfen güvenlik doğrulamasını tamamlayın.");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/login`, {
        username, password, turnstile_token: tsToken,
      });
      login(res.data.access_token);
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Giriş başarısız.");
      tsRef.current?.reset();
      setTsToken("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6 py-16" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logos/pt-plus-mark.png" alt="Pratik Teknoloji" className="mx-auto h-16 w-16 mb-5 drop-shadow-[0_0_20px_rgba(255,59,48,0.25)]" />
          <h1 className="font-display text-3xl font-bold text-white tracking-tight">Yönetim Paneli</h1>
          <p className="mt-2 text-white/50 text-sm">Pratik Teknoloji — yetkili giriş</p>
        </div>

        <form onSubmit={submit} className="rounded-3xl bg-white/[0.03] border border-white/10 p-8 space-y-5" data-testid="admin-login-form">
          <div>
            <label className="block text-[11px] font-bold tracking-[0.2em] uppercase text-white/50 mb-2">Kullanıcı Adı</label>
            <input
              autoFocus value={username} onChange={(e) => setUsername(e.target.value)}
              data-testid="admin-username"
              className="w-full bg-white/5 border border-white/10 focus:border-[#FF3B30] focus:bg-white/10 outline-none rounded-xl px-4 py-3 text-white placeholder-white/30 transition"
              placeholder="admin"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold tracking-[0.2em] uppercase text-white/50 mb-2">Şifre</label>
            <input
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password"
              className="w-full bg-white/5 border border-white/10 focus:border-[#FF3B30] focus:bg-white/10 outline-none rounded-xl px-4 py-3 text-white placeholder-white/30 transition"
              placeholder="••••••••"
            />
          </div>

          {siteKey && (
            <div className="flex justify-center" data-testid="admin-turnstile">
              <Turnstile
                ref={tsRef} siteKey={siteKey}
                onSuccess={(t) => setTsToken(t)} onError={() => setTsToken("")} onExpire={() => setTsToken("")}
                options={{ theme: "dark", language: "tr" }}
              />
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-xl bg-[#FF3B30]/10 border border-[#FF3B30]/30 p-3 text-sm text-[#FF6B63]" data-testid="admin-login-error">
              <AlertCircle size={16} className="flex-none mt-0.5" /><span>{error}</span>
            </div>
          )}

          <button
            type="submit" disabled={busy}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FF3B30] text-white px-7 py-4 font-semibold hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Lock size={16} /> {busy ? "Giriş yapılıyor..." : "Giriş Yap"}
          </button>
        </form>
      </div>
    </div>
  );
}
